import { createLogger } from "logging";
import { ValidationFailure } from "models/ValidationFailure";

const logger = createLogger("helpers/validation");

export function formatValidationFailuresAsHtmlList(failures: ValidationFailure[] | undefined): string | undefined {
  if (!failures || failures.length == 0) {
    return undefined;
  }

  return `
    <ul className="m-0">
      ${failures
        .map((error) => {
          return `<li>${error.errorMessage}</li>`;
        })
        .join("\n")}
    </ul>`;
}

/**
 * Checks if a file has Purview Information Protection or is encrypted
 * @param file The file to check
 * @returns true if the file has Purview protection or is encrypted (password-protected)
 */
export async function checkIfFileHasPurviewProtection(file: File): Promise<boolean> {
  try {
    const extension = file.name.toLowerCase().split(".").pop();

    // Check Office documents (DOCX, XLSX, PPTX, DOC, XLS, PPT)
    if (["docx", "xlsx", "pptx", "doc", "xls", "ppt"].includes(extension || "")) {
      // Read first bytes to check file signature
      const buffer = await file.slice(0, 8).arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Check for CFB (Compound File Binary) format: D0 CF 11 E0 A1 B1 1A E1
      // This is used by old Office formats (DOC, XLS, PPT) and encrypted OOXML files
      if (
        bytes.length >= 8 &&
        bytes[0] === 0xd0 &&
        bytes[1] === 0xcf &&
        bytes[2] === 0x11 &&
        bytes[3] === 0xe0 &&
        bytes[4] === 0xa1 &&
        bytes[5] === 0xb1 &&
        bytes[6] === 0x1a &&
        bytes[7] === 0xe1
      ) {
        // CFB format detected - this could be an old Office file or encrypted OOXML
        // Purview-protected files are stored as encrypted OOXML in CFB container
        logger.debug(`File ${file.name} has CFB signature - likely Purview-protected or encrypted`);
        return true;
      }

      // For modern Office files (DOCX, XLSX, PPTX), check if ZIP contains MIP custom properties
      if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
        // PK signature - it's a ZIP file (modern Office format)
        // Read more content to search for Purview/MIP markers
        const chunkSize = 64 * 1024; // Read 64KB
        const chunk = await file.slice(0, Math.min(chunkSize, file.size)).arrayBuffer();
        const text = new TextDecoder("utf-8", { fatal: false }).decode(chunk);

        // Check for Microsoft Information Protection custom XML parts
        // Purview labels are stored in customXml/item*.xml files with MSIP_ prefix
        if (
          text.includes("MSIP_Label_") ||
          (text.includes("customXml") && text.includes("MSIP")) ||
          text.includes("Microsoft.Security.RightsManagement")
        ) {
          logger.debug(`File ${file.name} contains Purview/MIP markers`);
          return true;
        }
      }
    }

    // Check PDF files for encryption
    if (extension === "pdf") {
      // Read more content to find /Encrypt dictionary - it can be anywhere in the file structure
      const chunkSize = Math.min(256 * 1024, file.size); // Read up to 256KB
      const pdfBuffer = await file.slice(0, chunkSize).arrayBuffer();
      const pdfText = new TextDecoder("latin1").decode(pdfBuffer); // Use latin1 for binary-safe decoding

      // Check for PDF signature: %PDF
      if (pdfText.startsWith("%PDF")) {
        // Any encrypted PDF is blocked (includes both password-protected and Purview-protected)
        // Check for various encryption indicators
        if (
          pdfText.includes("/Encrypt") ||
          pdfText.includes("/StmF") || // Stream filter - present in encrypted PDFs
          pdfText.includes("/StrF") || // String filter - present in encrypted PDFs
          pdfText.includes("/Perms") // Permissions - Microsoft Purview uses this
        ) {
          logger.debug(`File ${file.name} is an encrypted PDF`);
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    logger.error(`Error checking Purview protection for ${file.name}:`, error);
    // In case of error, allow the file (server-side validation will catch it)
    return false;
  }
}

export function validateUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

export const mapErrorsToObject = (errors?: Record<string, string[]>): Record<string, string> => {
  const safeErrors = errors || [];
  return Object.assign(
    {},
    ...Object.entries<string[]>(safeErrors).map(([key, errors]) => ({
      [key]: errors.join("\n"),
    }))
  );
};

export const mapAlertMessages = (errors: Record<string, string>, knownKeys: string[]): string[] => {
  logger.trace("mapAlertMessages(): ", errors, knownKeys);

  const result = Array<string>();
  Object.keys(errors).forEach((key) => {
    const keyLower = key.toLowerCase();
    const isUnknown = !knownKeys.includes(keyLower);
    logger.trace("mapAlertMessages(): ", keyLower, isUnknown);
    if (isUnknown) {
      errors[key].split("\n").forEach((x) => {
        if (x) {
          result.push(x);
        }
      });
    }
  });
  return result;
};
